import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@material-ui/styles';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardActions,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress
} from '@material-ui/core';
import { ActionButton } from 'components';

const useStyles = makeStyles(() => ({
  root: {},
  content: {
    padding: 0
  },
  actions: {
    justifyContent: 'flex-end'
  },
  rightCol: {
    textAlign: 'right'
  },
  tableRow: {
    cursor: 'pointer'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  }
}));

const SchemaTable = props => {
  const {
    className,
    isLoading,
    schemas,
    search,
    showActive,
    onEditSchema,
    onArchiveOrActivateSchema,
    onCopySchema,
    ...rest
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const rowsPerPage = useSelector(
    store => store.user.selectedRowsPerPage || 10
  );
  const { setRowsPerPage } = UserActions;
  const [page, setPage] = useState(0);
  const [displayedSchemas, setDisplayedSchemas] = useState(schemas);

  useEffect(() => {
    let filteredSchemas = schemas;
    if (search && search.length > 2) {
      filteredSchemas = filteredSchemas.filter(
        t =>
          t.schemaName &&
          t.schemaName.toLowerCase().indexOf(search.toLowerCase() || '') >= 0
      );
    }
    setPage(0);
    setDisplayedSchemas(filteredSchemas);
  }, [search, schemas]);

  const handleOnEditClick = schema => {
    onEditSchema(schema);
    history.push(`/schema/design/${schema.id}`);
  };

  const handlePageChange = (event, page) => {
    setPage(page);
  };

  const handleRowsPerPageChange = event => {
    dispatch(setRowsPerPage(event.target.value));
    setPage(0);
  };

  const onArchiveOrActivateClick = id => {
    onArchiveOrActivateSchema(id);
  };

  const onCopyClick = id => {
    onCopySchema(id);
  };

  const getActionButtonOptions = schema => [
    {
      name: 'Edit Schema',
      action: ['ACTION', () => handleOnEditClick(schema)]
    },
    {
      name: 'Copy Schema',
      action: ['ACTION', () => onCopyClick(schema.id)]
    },
    {
      name: showActive ? 'Archive Schema' : 'Activate Schema',
      action: ['ACTION', () => onArchiveOrActivateClick(schema.id)]
    }
  ];

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <CardContent className={classes.content}>
        <PerfectScrollbar>
          {isLoading && (
            <div className={classes.loader}>
              <CircularProgress />
            </div>
          )}
          {!isLoading && (
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    {schemaTableTitles.map(tableTitle => (
                      <TableCell key={tableTitle.id}>
                        {tableTitle.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedSchemas
                    .filter(c => c.active === showActive)
                    .slice(rowsPerPage * page, rowsPerPage * (page + 1))
                    .map(schema => (
                      <TableRow
                        className={classes.tableRow}
                        hover
                        key={schema.id}>
                        <TableCell>{schema.schemaName}</TableCell>
                        <TableCell>{schema.eventProductCategory}</TableCell>
                        <TableCell>{schema.eventProductName}</TableCell>
                        <TableCell>{schema.eventTypeCategory}</TableCell>
                        <TableCell className={classes.rightCol}>
                          <ActionButton
                            options={getActionButtonOptions(schema)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </PerfectScrollbar>
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={displayedSchemas.filter(c => c.active === showActive).length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </CardActions>
    </Card>
  );
};

export default SchemaTable;
